import type { HelpContent } from './entries.tr';
import type { AI_HELP_TR } from './entries.ai';

type Loc = Pick<HelpContent, 'title' | 'short'>;

/** Phase 9 (KI, Empfehlungen, Bedarfsprognose) — Hilfetitel und Kurztexte, Deutsch. Begriffe: docs/I18N_GLOSSARY.md. */
export const AI_HELP_DE: Record<keyof typeof AI_HELP_TR, Loc> = {
  'page.ai_settings': {
    title: 'KI-Einstellungen',
    short: 'KI ein-/ausschalten, monatliches Ausgabenlimit, Modelle, Preistabelle und Nutzungsbericht.',
  },
  'ai.enable': {
    title: 'KI einschalten',
    short:
      'Legen Sie ein monatliches Ausgabenlimit fest und bestätigen Sie, dass Sie einen kostenpflichtigen Dienst einschalten. Erst dann werden Anfragen gesendet.',
  },
  'ai.disable': {
    title: 'KI ausschalten',
    short: 'Es werden keine neuen Anfragen gesendet; Einstellungen und Nutzungsprotokoll bleiben erhalten.',
  },
  'ai.limits': {
    title: 'Ausgaben- und Anfragelimits',
    short:
      'Das Monatslimit sind die geschätzten Ausgaben in USD; das Tageslimit ist eine Anzahl von Anfragen.',
  },
  'ai.models': {
    title: 'Modelle',
    short: 'Textmodell für lange Texte, ein günstigeres Modell für kurze Aufgaben und das Bildmodell.',
  },
  'ai.prices': {
    title: 'Preistabelle',
    short:
      'Eingabe- und Ausgabepreis pro 1 Mio. Token (USD). Die Standardwerte stammen von den Preisseiten der Anbieter.',
  },
  'ai.usage': {
    title: 'Nutzungsbericht',
    short:
      'Jede gesendete Anfrage: Zweck, Modell, Ergebnis, Token, geschätzte Kosten, Benutzer, maskierte Daten und Gesundheitsaussagen.',
  },
  'ai.supportDrafts': {
    title: 'Kundennachricht in Support-Entwürfen',
    short:
      'Wenn eingeschaltet, wird die Nachricht des Kunden für Support-Antwortentwürfe maskiert gesendet. Standardmäßig aus.',
  },
  'page.ai_recommendations': {
    title: 'Produktempfehlungen',
    short:
      'Einstellungen und Ergebnisse von „Kunden kauften auch“, Warenkorbempfehlungen und „Zeit zum Nachbestellen“.',
  },
  'recommendations.run': {
    title: 'Jetzt berechnen',
    short: 'Berechnet neu, ohne auf den nächtlichen Job zu warten.',
  },
  'recommendations.pairs': {
    title: 'Stärkste Beziehungen',
    short: 'Verschiedene Käufer, Konfidenz und Lift der stärksten Beziehung jedes Produkts.',
  },
  'recommendations.settings': {
    title: 'Empfehlungseinstellungen',
    short:
      'Bereiche ein/aus, Zeitraum, Mindestanzahl verschiedener Käufer, Mindest-Konfidenz, Maximum pro Produkt, Händlerbestellungen.',
  },
  'page.ai_forecast': {
    title: 'Bedarfsprognose',
    short: 'Prognostizierter Absatz je Variante, Reichweite in Tagen, Bestellvorschlag und MHD-Risiko.',
  },
  'forecast.accuracy': {
    title: 'Prognosefehler (WAPE)',
    short:
      'Wie weit die vor 4 Wochen erstellte Prognose von den tatsächlichen Verkäufen der letzten 4 Wochen abwich. Niedriger ist besser.',
  },
  'forecast.table': {
    title: 'Prognoseliste',
    short:
      'Ausverkaufte und nachzubestellende Artikel zuerst. „Anpassen“ legt eine produktspezifische Lieferzeit fest oder schließt das Produkt aus.',
  },
  'forecast.on_order': {
    title: 'Bestellt',
    short:
      'Noch nicht eingegangene Menge aus an Lieferanten gesendeten Bestellungen; darunter die Menge aus noch nicht gesendeten Anfragen. Beides wird vom Vorschlag abgezogen. Die Lieferzeit kommt vom letzten Lieferanten, sofern keine produktspezifische festgelegt ist.',
  },
  'forecast.settings': {
    title: 'Prognose-Standardwerte',
    short:
      'Historie, Standard-Lieferzeit und Sicherheitstage, Überprüfungszeitraum, Schwelle für Überbestand und Händlerbestellungen.',
  },
  'page.content_studio': {
    title: 'Content-Studio',
    short:
      'Artikel in 27 Schritten erstellen: von Thema und Quellen über Gliederung, Entwurf, SEO und Bilder bis zur Qualitätsprüfung.',
  },
  'page.content_studio_project': {
    title: 'Artikelprojekt',
    short: 'Die 27 Schritte eines Artikels: welche erledigt sind und wo KI verwendet wurde.',
  },
  'studio.create': {
    title: 'Neues Artikelprojekt',
    short: 'Legt ein neues Projekt an; im Blog ändert sich nichts.',
  },
  'studio.brief': {
    title: 'Thema und Ziel',
    short: 'Beantwortete Frage, Zielgruppe, Zielland und Suchintention.',
  },
  'studio.keyword': {
    title: 'Keyword',
    short:
      'Die Suche, auf die der Artikel abzielt; bei Auswahl aus dem Pool wird auch die Suchintention übernommen.',
  },
  'studio.health': {
    title: 'Gesundheits-/Nahrungsergänzungsinhalt',
    short:
      'Quellen werden Pflicht, und der Blogbeitrag braucht die Freigabe durch den Tierärztlichen Fachprüfer.',
  },
  'studio.save': {
    title: 'Speichern',
    short: 'Speichert das Projekt; warnt, wenn zwischenzeitlich jemand anderes gespeichert hat.',
  },
  'studio.steps': {
    title: '27 Schritte',
    short: 'Aus den gespeicherten Daten berechnet; der Stern markiert Schritte, in denen KI verwendet wurde.',
  },
  'studio.sources': {
    title: 'Quellen',
    short: 'Bis zu 20 Quellen. Die KI darf nur diese zitieren und keine Quellen erfinden.',
  },
  'studio.verify': {
    title: 'Quellen prüfen',
    short:
      'Jede Quelle wird vom Server geöffnet: Erreichbarkeit, Weiterleitungsziel und Seitentitel. Interne Adressen werden blockiert.',
  },
  'studio.duplicates': {
    title: 'Dublettenprüfung',
    short:
      'Titelähnlichkeit mit Beiträgen und Seiten des Shops in derselben Sprache. Über 60 % gilt als Warnung.',
  },
  'studio.ai_plan': {
    title: 'Plan und Überschriften mit KI',
    short: 'Kostenpflichtige Anfrage; wird auf das Monatslimit angerechnet.',
  },
  'studio.ai_draft': {
    title: 'Entwurf mit KI',
    short: 'Markdown-Entwurf auf Basis der Überschriften. Gesundheitsaussagen werden aufgelistet.',
  },
  'studio.draft': {
    title: 'Entwurf',
    short:
      'Markdown; wird bei der Übertragung in das Blogdokument umgewandelt. Rohes HTML wird nicht ausgeführt.',
  },
  'studio.links': {
    title: 'Interne Links',
    short: 'Passende veröffentlichte Produkte, Kategorien und Beiträge (ohne KI).',
  },
  'studio.related': { title: 'Passende Produkte', short: 'Werden unter dem Blogbeitrag angezeigt (max. 8).' },
  'studio.ai_meta': {
    title: 'SEO-Vorschlag mit KI',
    short: 'SEO-Titel, Meta-Beschreibung, Slug und Schlagwörter (günstigeres Modell).',
  },
  'studio.seo': { title: 'SEO-Felder', short: 'SEO-Titel max. 70, Meta-Beschreibung max. 170 Zeichen.' },
  'studio.images': {
    title: 'Bilder',
    short: 'Titelbild und Alt-Text werden in den Blogbeitrag übernommen. Jedes Bild braucht einen Alt-Text.',
  },
  'studio.quality': {
    title: 'Qualitätsprüfung',
    short:
      'Längen, Wortzahl, Überschriftenstruktur, Keyword, Links, Quellen, Alt-Texte, Gesundheitsaussagen und Dubletten.',
  },
  'studio.export': {
    title: 'In Blogbeitrag übertragen',
    short:
      'Erstellt oder aktualisiert den Blog-Entwurf. Der veröffentlichte Beitrag ändert sich nicht; veröffentlicht wird im Bereich Blog.',
  },
  'studio.disclosure': {
    title: 'Hinweis auf KI-Unterstützung',
    short:
      'Wurde KI für Plan, Entwurf oder SEO verwendet, wird am Ende der Hinweis „mit KI-Unterstützung erstellt“ angefügt. Standardmäßig an.',
  },
  'studio.archive': {
    title: 'Projekt archivieren',
    short: 'Entfernt es aus der Liste; der Blogbeitrag ist nicht betroffen.',
  },
  'studio.performance': {
    title: 'Performance',
    short:
      'Search-Console-Daten des veröffentlichten Beitrags: letzte vs. vorherige 28 Tage und Suchanfragen.',
  },
  'studio.suggestions': {
    title: 'Aktualisierungsvorschläge',
    short:
      'Regelbasiert: veralteter Beitrag, Traffic-Rückgang, niedrige Klickrate, nahe der ersten Seite, nicht abgedeckte Suchanfragen, alte Quellenprüfung.',
  },
  'studio.ai_cover': {
    title: 'Titelbild mit KI',
    short:
      'Erzeugt aus Ihrer Beschreibung ein eigenes Titelbild (OpenAI Images). Kostenpflichtige Anfrage; die Obergrenze pro Bild steht in den Einstellungen.',
  },
  'studio.ai_image': {
    title: 'Inhaltsbild mit KI',
    short: 'Erzeugt ein eigenes Bild für den Artikeltext (max. 20).',
  },
  'studio.ai_alt': {
    title: 'Alt-Text mit KI',
    short:
      'Das Bild wird dem Modell für kurze Aufgaben gezeigt, das einen Alt-Text aus einem Satz vorschlägt. Prüfen Sie ihn vor dem Speichern.',
  },
  'studio.provenance': {
    title: 'Quelle und Lizenz',
    short:
      'Quelle, Lizenz, Bildnachweis und Quell-URL eines hochgeladenen Bildes. Die Quelle KI-generierter Bilder kann nicht geändert werden.',
  },
  'studio.insert_image': {
    title: 'In Entwurf einfügen',
    short:
      'Fügt das Bild als Zeile am Ende des Entwurfs ein; verschieben Sie es an die gewünschte Stelle. Im Blogbeitrag wird daraus ein Bild.',
  },
  'ai.draft_product': {
    title: 'Beschreibungsentwurf mit KI',
    short:
      'Schreibt Kurz- und Langbeschreibung aus den gespeicherten Produktdaten. Erfindet nichts; warnt vor Gesundheitsaussagen.',
  },
  'ai.draft_translate': {
    title: 'Mit KI übersetzen',
    short:
      'Übersetzt den Schwesterbeitrag in der gewählten Sprache in die Sprache dieses Beitrags; Links, Bilder und Struktur bleiben erhalten.',
  },
  'ai.draft_email': {
    title: 'Betreffideen mit KI',
    short: 'Drei Betreffzeilen, ein Preheader und ein Einleitungsabsatz.',
  },
  'ai.draft_support': {
    title: 'Antwortentwurf mit KI',
    short:
      'Entwirft eine Antwort aus Betreff, Kategorie, Bestellstatus und letzten Nachrichten des Tickets. Ausgeblendet, solange der Inhaber es nicht eingeschaltet hat.',
  },
  'ai.ad_suggestions': {
    title: 'Werbeempfehlungen',
    short:
      'Regelbasierte Befunde je Kampagne; „Mit KI zusammenfassen“ priorisiert sie nur anhand dieser Zahlen.',
  },
  'ai.draft_ad_copy': {
    title: 'Anzeigentext-Entwurf',
    short:
      'Texte für eine responsive Google-Suchanzeige oder eine Meta-Anzeige zum ausgewählten Produkt. Zeilen über dem Limit werden verworfen.',
  },
  'page.ai_copilot': {
    title: 'Copilot (Geschäftsassistent)',
    short:
      'Erkenntnisse aus Ihren Umsatz-, Margen-, Erstattungs-, Bestands-, SEO-, Segment- und Werbedaten sowie ein Assistent, der mit diesen Daten antwortet.',
  },
  'copilot.insights': {
    title: 'Erkenntnisse',
    short: 'Vergleich mit dem gleich langen Vorzeitraum und regelbasierte Befunde.',
  },
  'copilot.report': {
    title: 'Als Managementbericht kopieren',
    short: 'Kopiert die Erkenntnisse als reinen Text.',
  },
  'copilot.ask': {
    title: 'Copilot fragen',
    short:
      'Sendet die Frage und das berechnete Datenpaket. Kostenpflichtige Anfrage; führt keine Aktionen aus.',
  },
  'copilot.profit': {
    title: 'Nettogewinn',
    short:
      'Gewinnberechnung des Finanzberichts (ohne MwSt., türkische Lira) für diesen und den Vorzeitraum; nur mit Berechtigung für den Finanzbericht sichtbar.',
  },
  'page.ai_seo': {
    title: 'SEO-Vorschläge',
    short: 'Keyword-Cluster, Themenlandkarte, Long-Tail-Suchanfragen, Inhaltslücken und Google-Produkttitel.',
  },
  'seo_ai.clusters': {
    title: 'Automatische Cluster',
    short:
      'Keywords, deren Wortstämme sich mindestens zur Hälfte überschneiden, werden als ein Cluster vorgeschlagen.',
  },
  'seo_ai.topics': {
    title: 'Themenlandkarte',
    short:
      'Pillar-Seite, unterstützende Inhalte und ob unterstützende Beiträge auf die Pillar-Seite verlinken.',
  },
  'seo_ai.long_tail': {
    title: 'Long-Tail-Suchanfragen',
    short: 'Search-Console-Suchanfragen mit 3+ Wörtern und 10+ Impressionen, die nicht im Pool sind.',
  },
  'seo_ai.gaps': {
    title: 'Inhaltslücken',
    short: 'Pool-Keywords ohne Zielseite; legen Sie dafür ein Content-Studio-Projekt an.',
  },
  'seo_ai.merchant_titles': {
    title: 'Google-Produkttitel',
    short:
      'Titelregeln werden geprüft, mit regelbasiertem Vorschlag; optional KI-Vorschlag. Ohne Ihre Bestätigung wird nichts gespeichert.',
  },
  'page.ai_ltv': {
    title: 'Prognostizierter Kundenwert',
    short:
      'Erwartete Ausgaben in 12 Monaten (LTV), Durchschnitte je Segment, wertvollste Kunden und Vergleich mit den Werbekosten pro Neukunde.',
  },
  'ltv.model': {
    title: 'Modell',
    short: 'Eine Erwartung, keine Gewissheit; Saisonalität und Aktionen werden nicht modelliert.',
  },
  'ltv.segments': {
    title: 'Nach Segment',
    short: 'Durchschnittliche bisherige und erwartete 12-Monats-Ausgaben je RFM-Segment.',
  },
  'page.ai_youtube': {
    title: 'YouTube-Videos',
    short: 'Status, Dauer, Kanal und Verwendung der YouTube-Videos in Produkten und Blogbeiträgen.',
  },
  'youtube.connection': {
    title: 'YouTube-Verbindung',
    short:
      'Aktivieren Sie in Google Cloud die YouTube Data API v3, erstellen Sie einen API-Schlüssel und geben Sie ihn unter Integrationen ein.',
  },
  'youtube.sync': {
    title: 'Jetzt synchronisieren',
    short: 'Liest die Videodetails jetzt von YouTube (1 Kontingenteinheit pro 50 Videos).',
  },
  'youtube.videos': {
    title: 'Verwendete Videos',
    short: 'Videos mit Problemen stehen oben; entfernen oder ersetzen Sie sie.',
  },
};
